import {
  useState,
  useEffect,
  useMemo
} from "react";
import {
  FiEye,
  FiEdit2,
  FiTrash2
} from "react-icons/fi";
import {
  useNavigate
} from "react-router-dom";

import apiClient from "../../shared/API/apiClient";

import {
  useReportingYear
} from "../../context/ReportingYearContext";
import "../../styles/pages/record_entry_form.css";
import "../../styles/components/Pagination.css";
import ConfirmModal from "../../shared/components/ConfirmModal";
import usePermission from "../../shared/hooks/usePermission";



// =========================================
// HELPERS
// =========================================

const formatNumber = (
  value,
  decimals = 0
) => {

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  return Number(value).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }
  );
};

const formatDate = (
  dateString
) => {

  if (!dateString) {
    return "—";
  }

  return new Date(
    dateString
  ).toLocaleDateString(
    "en-GB",
    {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }
  );
};

// =========================================
// ACTIVITY ICONS
// =========================================

const getActivityIcon = (
  activityType = ""
) => {

  const type =
    activityType.toLowerCase();

  if (
    type.includes("diesel") ||
    type.includes("fuel")
  ) {
    return "🛢️";
  }

  if (
    type.includes("electricity") ||
    type.includes("power")
  ) {
    return "⚡";
  }

  if (
    type.includes("water")
  ) {
    return "💧";
  }

  if (
    type.includes("hazardous")
  ) {
    return "☣️";
  }

  if (
    type.includes("waste")
  ) {
    return "♻️";
  }

  if (
    type.includes("coal")
  ) {
    return "🪨";
  }

  if (
    type.includes("monitor")
  ) {
    return "📊";
  }

  return "🌿";
};

// =========================================
// PAGE
// =========================================

const ActivityRecordsPage = () => {

const [deleteId, setDeleteId] =
  useState(null);

const [deleting, setDeleting] =
  useState(false);
  // =========================================
  // NAVIGATION
  // =========================================

  const navigate =
    useNavigate();

  // =========================================
  // REPORTING YEAR
  // =========================================

  const {
    reportingYearId
  } = useReportingYear();

  // =========================================
  // STATES
  // =========================================

  const [loading, setLoading] =
    useState(false);

  const [
    activityRecords,
    setActivityRecords
  ] = useState([]);

  const [
    currentPage,
    setCurrentPage
  ] = useState(1);

  const [
    rowsPerPage,
    setRowsPerPage
  ] = useState(10);

  const [filters, setFilters] =
    useState({

      from_date: "",

      to_date: "",

      activity_type: "",

      location: "",

      status: ""

    });

  // =========================================
  // FETCH RECORDS
  // =========================================

  const fetchActivityRecords =
    async () => {

      try {

        setLoading(true);

        const response =
          await apiClient.get(
            "/api/activity-data",
            {
              params: {
                reporting_year_id:
                  reportingYearId
              }
            }
          );

        setActivityRecords(
          response.data?.data || []
        );

      } catch (err) {

        

      } finally {

        setLoading(false);
      }
    };

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {

    if (!reportingYearId) {
      return;
    }

    fetchActivityRecords();

  }, [reportingYearId]);

  // =========================================
  // FILTER OPTIONS
  // =========================================

  const activityTypeOptions =
    useMemo(() => {

      return [
        ...new Set(
          activityRecords.map(
            (item) =>
              item.activity_type
          )
        )
      ];

    }, [activityRecords]);

  const locationOptions =
    useMemo(() => {

      return [
        ...new Set(
          activityRecords.map(
            (item) =>
              item.location_name
          )
        )
      ];

    }, [activityRecords]);

  // =========================================
  // FILTERED DATA
  // =========================================

  const filteredRecords =
    activityRecords.filter(
      (item) => {

        const entryDate =
          new Date(
            item.entry_date
          );

        const fromDate =
          filters.from_date
            ? new Date(
                filters.from_date
              )
            : null;

        const toDate =
          filters.to_date
            ? new Date(
                filters.to_date
              )
            : null;

        const matchFrom =
          !fromDate ||
          entryDate >= fromDate;

        const matchTo =
          !toDate ||
          entryDate <= toDate;

        const matchActivity =
          !filters.activity_type ||
          item.activity_type ===
            filters.activity_type;

        const matchLocation =
          !filters.location ||
          item.location_name ===
            filters.location;

        const matchStatus =
          !filters.status ||
          item.status ===
            filters.status;

        return (
          matchFrom &&
          matchTo &&
          matchActivity &&
          matchLocation &&
          matchStatus
        );
      }
    );

  // =========================================
  // PAGINATION
  // =========================================

  const totalPages =
    Math.ceil(
      filteredRecords.length /
      rowsPerPage
    );

  const startIndex =
    (
      currentPage - 1
    ) * rowsPerPage;

  const endIndex =
    startIndex +
    rowsPerPage;

  const paginatedRecords =
    filteredRecords.slice(
      startIndex,
      endIndex
    );

  // =========================================
  // DELETE
  // =========================================
    
const handleDeleteClick = (
  id
) => {

  setDeleteId(id);

};
const confirmDelete =
  async () => {

    try {

      setDeleting(true);

      
await apiClient.delete(
  `/api/activity-data/delete`,
  {
    data: {
      activity_data_id: deleteId
    }
  }
);
      fetchActivityRecords();

      setDeleteId(null);

    } catch (err) {

      console.error(err);

    } finally {

      setDeleting(false);

    }
  };
  // =========================================
  // STATUS CLASS
  // =========================================

  const getStatusClass =
    (status) => {

      if (
        status?.toLowerCase() ===
        "draft"
      ) {

        return "fa-solid fa-pen-to-square st-badge__icon";
      }

      return "fa-solid fa-paper-plane st-badge__icon";
    };

  // =========================================
  // RENDER
  // =========================================
//Access Permission
const { hasPermission } = usePermission();
  return (

    <div className="dash-page">

      {/* HEADER */}

      <div className="dash-page-header">

  <div>

    <div className="dash-page-subtitle">
      ENVIRONMENT & CARBON
    </div>

    <div className="dash-page-title">
      Activity Records
    </div>

    <div className="dash-page-description">
      All recorded environment and carbon activity entries.
    </div>

  </div>
{
    hasPermission("record_activity","create") && (

  <button
    className="org-btn-save"
    onClick={() =>
      navigate(
        "/dashboard/record-activity"
      )
    }
  >
    + Record Activity
  </button>
 )
}
</div>

      {/* FILTER CARD */}

      <div className="le-card">

        <div className="ecf-filter-title">
          ▼ Filters
        </div>

        <div
          className="ecf-filter-grid"
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(5, 1fr)",
            gap: 20,
            alignItems: "end",
            marginTop: 20
          }}
        >

          {/* FROM */}

          <div className="org-form-field">

            <label className="org-field-label">
              FROM
            </label>

            <input
              type="date"
              className="org-field-input"
              value={
                filters.from_date
              }
              onChange={(e) =>
                setFilters({
                  ...filters,
                  from_date:
                    e.target.value
                })
              }
            />

          </div>

          {/* TO */}

          <div className="org-form-field">

            <label className="org-field-label">
              TO
            </label>

            <input
              type="date"
              className="org-field-input"
              value={
                filters.to_date
              }
              onChange={(e) =>
                setFilters({
                  ...filters,
                  to_date:
                    e.target.value
                })
              }
            />

          </div>

          {/* ACTIVITY */}

          <div className="org-form-field">

            <label className="org-field-label">
              ACTIVITY TYPE
            </label>

            <select
              className="org-field-input"
              value={
                filters.activity_type
              }
              onChange={(e) =>
                setFilters({
                  ...filters,
                  activity_type:
                    e.target.value
                })
              }
            >

              <option value="">
                All Activities
              </option>

              {activityTypeOptions.map(
                (item) => (

                  <option
                    key={item}
                    value={item}
                  >
                    {item}
                  </option>

                )
              )}

            </select>

          </div>

          {/* LOCATION */}

          <div className="org-form-field">

            <label className="org-field-label">
              LOCATION
            </label>

            <select
              className="org-field-input"
              value={
                filters.location
              }
              onChange={(e) =>
                setFilters({
                  ...filters,
                  location:
                    e.target.value
                })
              }
            >

              <option value="">
                All Locations
              </option>

              {locationOptions.map(
                (item) => (

                  <option
                    key={item}
                    value={item}
                  >
                    {item}
                  </option>

                )
              )}

            </select>

          </div>

          {/* STATUS */}

          <div className="org-form-field">

            <label className="org-field-label">
              STATUS
            </label>

            <select
              className="org-field-input"
              value={
                filters.status
              }
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status:
                    e.target.value
                })
              }
            >

              <option value="">
                All Statuses
              </option>

              <option value="Draft">
                Draft
              </option>

              <option value="Submitted">
                Submitted
              </option>

            </select>

          </div>

        </div>

      </div>

      {/* TABLE */}

      <div
        className="le-card"
        style={{
          marginTop: 20
        }}
      >

        {/* HEADER */}

        <div className="ecf-table-header">

          <div className="ecf-table-title">
            All Entries
          </div>

          <div className="ecf-total-chip">
            {
              filteredRecords.length
            } total
          </div>

        </div>

        {/* TABLE */}

        <div className="ecf-table-wrapper">

          <table className="ecf-table">

            <thead>

              <tr>

                <th>DATE</th>

                <th>
                  ACTIVITY TYPE
                </th>

                <th>LOCATION</th>

                <th>VALUE</th>

                <th>UNIT</th>

                <th>
                  EMISSION (tCO₂e)
                </th>

                <th>STATUS</th>

                <th>ACTIONS</th>

              </tr>

            </thead>

            <tbody>

              {!loading &&
                paginatedRecords.length === 0 && (

                  <tr>

                    <td
                      colSpan="8"
                      style={{
                        textAlign:
                          "center",
                        padding: 40
                      }}
                    >
                      No activity records found.
                    </td>

                  </tr>

                )}

              {paginatedRecords.map(
                (item) => (

                  <tr key={item.id}>

                    <td>
                      {formatDate(
                        item.entry_date
                      )}
                    </td>

                    <td>

                      <div className="ecf-activity-cell">

                        <span>
                          {getActivityIcon(
                            item.activity_type
                          )}
                        </span>

                        <span>
                          {
                            item.activity_type
                          }
                        </span>

                      </div>

                    </td>

                    <td>
                      {
                        item.location_name
                      }
                    </td>

                    <td
                      style={{
                        fontWeight: 700
                      }}
                    >
                      {formatNumber(
                        item.consumption_value,
                        0
                      )}
                    </td>

                    <td>
                      {item.unit}
                    </td>

                    <td
                      style={{
                        fontWeight: 700
                      }}
                    >
                      {formatNumber(
                        item.emission_value,
                        2
                      )}
                    </td>

                    <td>

                      <span
                        className="ecf-status" 
                      >
                      <i className={`${getStatusClass(
                          item.status
                        )}`}/>
                        {item.status}

                      </span>

                    </td>

                    <td>

                      <div className="ecf-action-buttons">

  {item.status?.toLowerCase() ===
  "draft" ? (

    <>

      {/* EDIT */}

      <button
        className="ecf-icon-btn ecf-edit-btn"
        onClick={() =>
          navigate(
            `/dashboard/edit-activity/${item.id}`
          )
        }
        title="Edit Activity"
      >
        <FiEdit2 size={16} />
      </button>

      {/* DELETE */}

      <button
        className="ecf-icon-btn ecf-delete-btn"
        onClick={() =>
          handleDeleteClick(item.id)
        }
        title="Delete Activity"
      >
        <FiTrash2 size={16} />
      </button>

    </>

  ) : (

    /* VIEW */

    <button
      className="ecf-icon-btn ecf-view-btn"
      onClick={() =>
        navigate(
          `/dashboard/view-activity/${item.id}`
        )
      }
      title="View Activity"
    >
      <FiEye size={18} />
    </button>

  )}

</div>

                    </td>

                  </tr>

                )
              )}

            </tbody>

          </table>

        </div>

        {/* FOOTER */}

        {/* FOOTER */}

<div className="pg-bar">

  {/* LEFT */}

  <div className="pg-info">

    <span className="pg-count">

      Showing&nbsp;

      <strong>
        {filteredRecords.length === 0
          ? 0
          : startIndex + 1}

        –

        {Math.min(
          endIndex,
          filteredRecords.length
        )}
      </strong>

      &nbsp;of&nbsp;

      <strong>
        {filteredRecords.length}
      </strong>

    </span>

    <label className="pg-size-label">

      Rows&nbsp;

      <select
        className="pg-size-select"
        value={rowsPerPage}
        onChange={(e) => {

          setRowsPerPage(
            Number(e.target.value)
          );

          setCurrentPage(1);
        }}
      >

        <option value={5}>
          5
        </option>

        <option value={10}>
          10
        </option>

        <option value={20}>
          20
        </option>

        <option value={50}>
          50
        </option>

      </select>

    </label>

  </div>

  {/* RIGHT */}

  <div className="pg-controls">

    {/* PREVIOUS */}

    <button
      className="pg-btn pg-nav"
      disabled={currentPage === 1}
      aria-label="Previous page"
      onClick={() =>
        setCurrentPage(
          currentPage - 1
        )
      }
    >
      ‹
    </button>

    {/* PAGE NUMBERS */}

    {Array.from(
      { length: totalPages },
      (_, i) => i + 1
    )
      .filter((page) => {

        if (totalPages <= 7) {
          return true;
        }

        if (
          page === 1 ||
          page === totalPages
        ) {
          return true;
        }

        if (
          page >= currentPage - 1 &&
          page <= currentPage + 1
        ) {
          return true;
        }

        return false;
      })
      .map((page, index, arr) => {

        const prevPage =
          arr[index - 1];

        const showDots =
          prevPage &&
          page - prevPage > 1;

        return (
          <div
            key={page}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
          >

            {showDots && (
              <span
                style={{
                  padding: "0 4px"
                }}
              >
                ...
              </span>
            )}

            <button
              className={`pg-btn pg-page ${
                currentPage === page
                  ? "pg-active"
                  : ""
              }`}
              aria-current={
                currentPage === page
                  ? "page"
                  : undefined
              }
              onClick={() =>
                setCurrentPage(page)
              }
            >
              {page}
            </button>

          </div>
        );
      })}

    {/* NEXT */}

    <button
      className="pg-btn pg-nav"
      disabled={
        currentPage === totalPages ||
        totalPages === 0
      }
      aria-label="Next page"
      onClick={() =>
        setCurrentPage(
          currentPage + 1
        )
      }
    >
      ›
    </button>

  </div>

</div>

      </div>
{deleteId && (

  <ConfirmModal
    title="Delete Activity"
    message="Are you sure you want to delete this activity? This action cannot be undone."
    confirmText="Delete"
    loading={deleting}
    onClose={() =>
      setDeleteId(null)
    }
    onConfirm={confirmDelete}
  />

)}
    </div>
  );
};

export default ActivityRecordsPage;